import { useMemo, useState } from 'react';
import { goBack, replaceRoute, resetNav } from '../../app/navigation';
import { defaultHousePlan, floorCountOf, OTHER_PRESET, planCounts } from '../../domain/housePlan';
import { displayAddress, type V2Area } from '../../domain/inspection';
import { useT } from '../../i18n/i18n';
import { floorLabelFor, planNamingFor, planSummaryFor, propertyTypeLabel } from '../../i18n/labels';
import { useStore } from '../../state/createStore';
import { formalWorkflowBlocked } from '../../domain/inspectionContext';
import { formalWorkflowGateFor } from '../../state/formalWorkflow';
import { applyHousePlanAction, areasOf, inspectionStore } from '../../state/inspectionStore';
import { FormalWorkflowGateCard } from './FormalWorkflowGateCard';
import { ActionSheet, Button, Card, EmptyState, SectionEyebrow, TopBar } from '../../ui/components';
import { AddAreaSheet } from '../areas/AddAreaSheet';
import { AreaGroups } from '../areas/AreaListScreen';
import { AreaOptionsSheet } from '../areas/AreaOptionsSheet';

// Property Layout (Figma 03 / 中文「08 房屋布局」· Authority §6.4): the structure editing view of an
// inspection — top bar with 完成 (→ Inspection Overview), a one-line hint, floor / catalog groups
// with ⊕ and a "添加区域" box, rows with drag handle · name · pencil · trash, and one outlined
// "+ 添加楼层或自定义区域" at the end. The Suggested Layout card (D-04: a local house-plan preset,
// no AI) appears only while the inspection has no areas AND the property type is known.
type AddState = { floor: number | null | undefined; presetKey?: string } | null;
type OptionsState = { area: V2Area; intent: 'menu' | 'rename' | 'delete' } | null;

export function PropertyLayoutScreen(props: { inspectionId: string }) {
  const { t, tp, locale } = useT();
  const state = useStore(inspectionStore, (s) => s);
  const inspection = state.inspections.find((i) => i.id === props.inspectionId) ?? null;
  const areas = useMemo(() => areasOf(state, props.inspectionId), [state, props.inspectionId]);
  const [add, setAdd] = useState<AddState>(null);
  const [addFloorOpen, setAddFloorOpen] = useState(false);
  const [options, setOptions] = useState<OptionsState>(null);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);

  if (!inspection) {
    return (
      <div className="screen">
        <TopBar title={t('layout.title')} onBack={() => resetNav({ screen: 'inspectionList' })} />
        <EmptyState
          title={t('inspection.notFoundTitle')}
          body={t('inspection.notFoundBody')}
          action={<Button onClick={() => resetNav({ screen: 'inspectionList' })}>{t('inspection.backToInspections')}</Button>}
        />
      </div>
    );
  }

  // NY F1 Round 1 (OC-1 / §15.1 effect 1). Property Layout is the structure-EDITING surface:
  // "Use Suggested Layout" and "+ Add Area" both seed areas AND their NY checklist rows, which is
  // precisely what the block forbids. So it is gated exactly like the area workspace, and the same
  // card asks the same questions in the same words. Reading is untouched — Home, Overview, Details,
  // Photo Pool, Findings and Reports all still open on a blocked inspection, and Back still works.
  const gate = formalWorkflowGateFor(inspection);
  if (formalWorkflowBlocked(gate)) {
    return (
      <div className="screen" data-testid="property-layout-gated">
        <TopBar title={t('layout.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} />
        <div className="screen-body">
          <FormalWorkflowGateCard inspection={inspection} gate={gate} />
        </div>
      </div>
    );
  }

  const nextFloor = floorCountOf(areas) + 1;
  const propertyType = inspection.property.propertyType;
  const suggested = propertyType ? defaultHousePlan(propertyType) : null;

  async function useSuggestedLayout() {
    if (!suggested) return;
    await applyHousePlanAction(props.inspectionId, suggested, planNamingFor(locale));
  }

  const done = (
    <button type="button" className="topbar-done" onClick={() => replaceRoute({ screen: 'inspection', inspectionId: props.inspectionId })} data-testid="layout-continue">
      {t('layout.done')}
    </button>
  );

  return (
    <div className="screen" data-testid="property-layout">
      <TopBar title={t('layout.title')} subtitle={displayAddress(inspection, t('inspection.noAddress'))} onBack={goBack} right={done} status={null} avatar={false} />
      <div className="screen-body">
        <p className="layout-hint" data-testid="layout-hint">
          {t('layout.hint')}
        </p>
        {areas.length === 0 && suggested && propertyType && !suggestionDismissed ? (
          <Card tone="low" className="suggested-card" testId="suggested-layout">
            <SectionEyebrow tone="primary">{t('layout.suggestedTitle')}</SectionEyebrow>
            <p className="card-body">
              {t('layout.suggestedBody', {
                type: propertyTypeLabel(t, propertyType),
                floors: planSummaryFor({ t, tp, locale }, suggested),
                areas: tp('common.areasCount', planCounts(suggested).areas)
              })}
            </p>
            <div className="suggested-actions">
              <Button variant="secondary" icon="layers" onClick={() => void useSuggestedLayout()} testId="use-suggested-layout">
                {t('layout.useSuggested')}
              </Button>
              <Button variant="text" onClick={() => setSuggestionDismissed(true)} testId="adjust-later">
                {t('layout.adjustLater')}
              </Button>
            </div>
          </Card>
        ) : null}

        <AreaGroups
          inspectionId={props.inspectionId}
          mode="layout"
          onOptions={(area) => setOptions({ area, intent: 'menu' })}
          onRename={(area) => setOptions({ area, intent: 'rename' })}
          onDelete={(area) => setOptions({ area, intent: 'delete' })}
          onAddArea={(floor) => setAdd({ floor })}
        />

        <div className="layout-add-more">
          <Button variant="outline" icon="add" onClick={() => setAddFloorOpen(true)} testId="add-floor-or-outbuilding">
            {t('layout.addFloorOrCustom')}
          </Button>
        </div>
      </div>

      {add ? <AddAreaSheet inspectionId={props.inspectionId} initialFloor={add.floor} initialPresetKey={add.presetKey} onClose={() => setAdd(null)} /> : null}

      {addFloorOpen ? (
        <ActionSheet
          title={t('layout.addFloorOrCustom')}
          onClose={() => setAddFloorOpen(false)}
          testId="add-floor-sheet"
          items={[
            {
              key: 'floor',
              label: t('layout.optionNewFloor', { floor: floorLabelFor(t, nextFloor) }),
              icon: 'stairs',
              onSelect: () => {
                setAddFloorOpen(false);
                setAdd({ floor: nextFloor, presetKey: 'bedroom' });
              },
              testId: 'add-floor-option-floor'
            },
            {
              key: 'outbuilding',
              label: t('layout.optionOutbuilding'),
              icon: 'cottage',
              onSelect: () => {
                setAddFloorOpen(false);
                setAdd({ floor: null, presetKey: 'detachedGarage' });
              },
              testId: 'add-floor-option-outbuilding'
            },
            {
              key: 'custom',
              label: t('layout.optionCustom'),
              icon: 'edit',
              onSelect: () => {
                setAddFloorOpen(false);
                setAdd({ floor: undefined, presetKey: OTHER_PRESET.key });
              },
              testId: 'add-floor-option-custom'
            }
          ]}
        >
          <p className="card-note">{t('layout.addFloorBody')}</p>
        </ActionSheet>
      ) : null}

      {options ? <AreaOptionsSheet key={`${options.area.id}-${options.intent}`} area={options.area} intent={options.intent} onClose={() => setOptions(null)} /> : null}
    </div>
  );
}
